import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Home, RefreshCw, X, Calendar } from 'lucide-react';
import api from '../../services/api';
import { clientAuthConfig } from '../../utils/clientAuthConfig';
import {
  formatBuyerCriteriaLine,
  getBuyerNarrativeSummary,
} from '../../utils/buyerProfileDisplay';
import { getStoredConversationId } from './convStorage';
import { buildAskPropertyChatPath } from '../../utils/askPropertyChatPath';

// Matches with no action taken (not dismissed) roll off the active "new for
// you" feed after this many days so a month-long search doesn't stay cluttered
// with stale suggestions — they're not lost, just folded into "Older matches".
const MATCH_STALE_DAYS = 30;

export function useBuyerPortalData(slug, client, reload) {
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [matching, setMatching] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [matches, setMatches] = useState([]);
  const [unseenCount, setUnseenCount] = useState(0);
  const [matchMsg, setMatchMsg] = useState(null);
  const [showings, setShowings] = useState([]);
  const [showingsError, setShowingsError] = useState(false);
  const [matchesError, setMatchesError] = useState(false);
  const [savedError, setSavedError] = useState(false);
  const [savedProperties, setSavedProperties] = useState([]);
  const [profile, setProfile] = useState({
    price_min: '',
    price_max: '',
    bedrooms_min: '',
    bathrooms_min: '',
    property_type: '',
    pre_approved: '',
    has_lawyer: '',
    has_mortgage_broker: '',
    areas: '',
    must_haves: '',
    deal_breakers: '',
    nice_to_haves: '',
    timeline: '',
    matching_enabled: true,
    alert_email: true,
  });
  const profileDirtyRef = useRef(false);

  const authCfg = useCallback(() => clientAuthConfig(slug), [slug]);

  const loadShowings = useCallback(async () => {
    try {
      const res = await api.get('/clients/me/showings', authCfg());
      setShowings(res.data.showings || []);
      setShowingsError(false);
    } catch {
      setShowingsError(true);
    }
  }, [authCfg]);

  const loadPropertyStates = useCallback(async () => {
    try {
      const res = await api.get('/clients/me/property-states', authCfg());
      setSavedProperties(
        (res.data.states || []).filter((s) => s.state === 'saved' || s.state === 'loved')
      );
      setSavedError(false);
    } catch {
      setSavedError(true);
    }
  }, [authCfg]);

  const clearPropertyState = useCallback(async (propertyId) => {
    setSavedProperties((list) => list.filter((s) => s.property_id !== propertyId));
    try {
      await api.delete(`/clients/me/property-state/${propertyId}`, authCfg());
    } catch {
      loadPropertyStates();
    }
  }, [authCfg, loadPropertyStates]);

  const loadMatches = useCallback(async () => {
    try {
      const res = await api.get('/clients/me/matches', authCfg());
      setMatches(res.data.matches || []);
      setUnseenCount(res.data.unseen_count || 0);
      setMatchesError(false);
    } catch {
      setMatchesError(true);
    }
  }, [authCfg]);

  const runMatching = useCallback(async () => {
    setMatching(true);
    setMatchMsg(null);
    try {
      const res = await api.post('/clients/me/run-matching', null, authCfg());
      setMatches(res.data.matches || []);
      setUnseenCount(res.data.unseen_count || 0);
      if (res.data.skipped === 'no_criteria') {
        setMatchMsg({ text: 'Add budget or areas in your criteria to find matches.', variant: 'info' });
      } else if (res.data.new_matches > 0) {
        setMatchMsg({
          text: `${res.data.new_matches} new match${res.data.new_matches > 1 ? 'es' : ''} found.`,
          variant: 'ok',
        });
      } else if ((res.data.matches || []).length === 0) {
        setMatchMsg({
          text: "No new matches right now — we'll check again when you save criteria.",
          variant: 'info',
        });
      } else {
        setMatchMsg(null);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setMatchMsg({ text: 'Session expired — please sign in again.', variant: 'error' });
      } else if (status === 429 || (status >= 500 && status < 600) || !err?.response) {
        setMatchMsg({
          text: 'Matching temporarily unavailable — try again in a moment.',
          variant: 'error',
        });
      } else {
        setMatchMsg({ text: 'Could not run matching.', variant: 'error' });
      }
    } finally {
      setMatching(false);
    }
  }, [authCfg]);

  const loadShowingsRef = useRef(loadShowings);
  const loadMatchesRef = useRef(loadMatches);
  const loadPropertyStatesRef = useRef(loadPropertyStates);
  loadShowingsRef.current = loadShowings;
  loadMatchesRef.current = loadMatches;
  loadPropertyStatesRef.current = loadPropertyStates;

  const isBuyer = client?.client_role === 'buyer' || client?.client_role === 'both';

  // Boot once per slug + agent_client_id — not on every client object refresh from reload().
  // Load the matches already on file. Do not search or email on every visit;
  // Refresh and Save criteria still run a search. Do not auto sync-from-chat
  // (stale stored conversation ids resurrect old criteria).
  useEffect(() => {
    if (!isBuyer || !client?.agent_client_id || !slug) return;
    loadShowingsRef.current();
    loadMatchesRef.current();
  }, [isBuyer, client?.agent_client_id, slug]);

  useEffect(() => {
    if (!isBuyer || !client?.agent_client_id || !slug) return;
    loadPropertyStatesRef.current();
  }, [isBuyer, client?.agent_client_id, slug]);

  useEffect(() => {
    if (!client?.buyer_profile || profileDirtyRef.current) return;
    const bp = client.buyer_profile;
    setProfile({
      price_min: bp.price_min ?? '',
      price_max: bp.price_max ?? '',
      bedrooms_min: bp.bedrooms_min ?? '',
      bathrooms_min: bp.bathrooms_min ?? '',
      property_type: Array.isArray(bp.property_types) && bp.property_types.length ? bp.property_types[0] : '',
      pre_approved: bp.pre_approved ?? '',
      has_lawyer: bp.has_lawyer ?? '',
      has_mortgage_broker: bp.has_mortgage_broker ?? '',
      areas: Array.isArray(bp.areas) ? bp.areas.join(', ') : '',
      must_haves: Array.isArray(bp.must_haves) ? bp.must_haves.join(', ') : '',
      deal_breakers: Array.isArray(bp.deal_breakers) ? bp.deal_breakers.join(', ') : '',
      nice_to_haves: Array.isArray(bp.nice_to_haves) ? bp.nice_to_haves.join(', ') : '',
      timeline: bp.timeline ?? '',
      matching_enabled: bp.matching_enabled !== false,
      alert_email: bp.alert_email !== false,
    });
  }, [client?.agent_client_id, client?.buyer_profile]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    profileDirtyRef.current = true;
    setProfile((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSyncFromChat = async (aiName) => {
    const conversationId = getStoredConversationId(slug, 'buyer');
    if (!conversationId) {
      setSaveMsg(`No linked chat found — message ${aiName} first, then try again.`);
      return;
    }
    setSyncing(true);
    setSaveMsg(null);
    try {
      const res = await api.post(
        '/clients/sync-from-chat',
        { conversation_id: conversationId },
        authCfg()
      );
      setShowings(res.data.showings || []);
      profileDirtyRef.current = false;
      await reload();
      await runMatching();
      const n = (res.data.showings || []).length;
      setSaveMsg(
        n > 0
          ? `Updated from chat — ${n} showing request${n > 1 ? 's' : ''} on file.`
          : 'Updated search criteria from your latest chat.'
      );
    } catch {
      setSaveMsg('Could not read criteria from chat.');
    } finally {
      setSyncing(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveMsg(null);
    try {
      const areas = profile.areas.split(',').map((s) => s.trim()).filter(Boolean);
      const mustHaves = profile.must_haves.split(',').map((s) => s.trim()).filter(Boolean);
      const dealBreakers = profile.deal_breakers.split(',').map((s) => s.trim()).filter(Boolean);
      const niceToHaves = profile.nice_to_haves.split(',').map((s) => s.trim()).filter(Boolean);
      const numOrNull = (v) => (v === '' || v == null || Number.isNaN(Number(v)) ? null : Number(v));
      await api.patch('/clients/me/buyer-profile', {
        price_min: numOrNull(profile.price_min),
        price_max: numOrNull(profile.price_max),
        bedrooms_min: numOrNull(profile.bedrooms_min),
        bathrooms_min: numOrNull(profile.bathrooms_min),
        property_types: profile.property_type ? [profile.property_type] : null,
        pre_approved: profile.pre_approved || null,
        has_lawyer: profile.has_lawyer || null,
        has_mortgage_broker: profile.has_mortgage_broker || null,
        areas,
        must_haves: mustHaves,
        deal_breakers: dealBreakers,
        nice_to_haves: niceToHaves,
        timeline: profile.timeline || null,
        matching_enabled: profile.matching_enabled,
        alert_email: profile.alert_email,
      }, authCfg());
      profileDirtyRef.current = false;
      await reload();
      await runMatching();
      setSaveMsg('Saved — searching for matching listings.');
    } catch {
      setSaveMsg('Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const markSeen = async (matchId) => {
    await api.patch(`/clients/me/matches/${matchId}/seen`, null, authCfg());
    setMatches((m) => m.map((x) => (x.id === matchId ? { ...x, seen_at: new Date().toISOString() } : x)));
    setUnseenCount((c) => Math.max(0, c - 1));
  };

  const dismissMatch = async (matchId) => {
    await api.patch(`/clients/me/matches/${matchId}/dismiss`, null, authCfg());
    setMatches((m) => m.filter((x) => x.id !== matchId));
  };

  const markAllSeen = async () => {
    await api.post('/clients/me/matches/mark-all-seen', null, authCfg());
    setMatches((m) => m.map((x) => ({ ...x, seen_at: x.seen_at || new Date().toISOString() })));
    setUnseenCount(0);
  };

  const [referralSent, setReferralSent] = useState({});
  const sendReferralRequest = async (kind) => {
    const label = kind === 'lawyer' ? 'a real estate lawyer' : 'a mortgage broker';
    try {
      await api.post(
        '/clients/me/messages',
        { content: `I don't have ${label} yet — could you recommend someone?` },
        authCfg()
      );
      setReferralSent((s) => ({ ...s, [kind]: true }));
    } catch {
      /* the message box below still works as a fallback */
    }
  };

  return {
    saving,
    syncing,
    matching,
    saveMsg,
    matches,
    unseenCount,
    matchMsg,
    showings,
    profile,
    handleChange,
    handleSyncFromChat,
    handleSave,
    markSeen,
    dismissMatch,
    markAllSeen,
    referralSent,
    sendReferralRequest,
    runMatching,
    savedProperties,
    clearPropertyState,
    showingsError,
    matchesError,
    savedError,
  };
}

function MatchCard({ match: m, slug, aiName, agentName, markSeen, dismissMatch }) {
  return (
    <li className={`client-match-card${!m.seen_at ? ' client-match-card--new' : ''}`}>
      <div className="client-match-card__main">
        <div className="client-match-card__address">
          {m.property.address}, {m.property.city}
          {m.property.is_own_listing && (
            <span className="client-match-card__own"> {agentName.split(' ')[0]}&apos;s listing</span>
          )}
        </div>
        <div className="client-match-card__meta">
          ${Number(m.property.list_price).toLocaleString()}
          {' · '}{m.property.bedrooms} bed · {m.property.bathrooms} bath
          {m.property.square_feet ? ` · ${m.property.square_feet} sqft` : ''}
        </div>
        {m.reason && <div className="client-match-card__reason">{m.reason}</div>}
      </div>
      <div className="client-match-card__actions">
        <Link
          to={buildAskPropertyChatPath(slug, {
            propertyId: m.property?.id,
            mlsNumber: m.property?.mls_number,
          })}
          className="client-match-card__cta"
          onClick={() => markSeen(m.id)}
        >
          Ask {aiName}
        </Link>
        <button type="button" className="client-match-card__dismiss" onClick={() => dismissMatch(m.id)} aria-label="Dismiss">
          <X size={16} />
        </button>
      </div>
    </li>
  );
}

function wishText(items) {
  const list = Array.isArray(items) ? items.map((item) => String(item).trim()).filter(Boolean) : [];
  return list.length ? list.join(', ') : 'None yet';
}

const SHOWING_STATUS_LABELS = {
  pending: 'Waiting for agent confirmation',
  confirmation_sent: 'Confirmed — check your email for details',
  confirmed: 'Confirmed',
  scheduled: 'Confirmed',
  completed: 'Completed',
  no_show: 'Missed',
  cancelled: 'Cancelled',
};

const CLOSED_SHOWING = new Set(['cancelled', 'completed', 'no_show']);

function isClosedShowing(showing) {
  return CLOSED_SHOWING.has(showing.status);
}

function ShowingCard({ showing: s }) {
  const when = s.scheduled_at ? new Date(s.scheduled_at) : null;

  return (
    <li className="client-showing-card">
      <div className="client-showing-card__address">
        {s.property?.address || 'Property'}
        {s.property?.city ? `, ${s.property.city}` : ''}
      </div>
      {when && (
        <div className="client-showing-card__when">
          {when.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
          {' at '}
          {when.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
        </div>
      )}
      <div className="client-showing-card__meta">
        {s.property?.list_price != null && (
          <>${Number(s.property.list_price).toLocaleString()}{' · '}</>
        )}
        {s.property?.mls_number && <>MLS® {s.property.mls_number} · </>}
        <span className={`client-showing-card__status client-showing-card__status--${s.status}`}>
          {SHOWING_STATUS_LABELS[s.status] || s.status}
        </span>
        {' · '}{when ? `Requested ${new Date(s.created_at).toLocaleDateString()}` : new Date(s.created_at).toLocaleDateString()}
      </div>
      {s.notes && <div className="client-showing-card__notes">{s.notes}</div>}
    </li>
  );
}

export function BuyerOverviewStrip({ client, buyerData }) {
  const { showings, unseenCount, savedProperties, showingsError, matchesError, savedError } = buyerData;
  const aiName = client.agent.assistant_name || 'Ava';
  const activeShowings = showings.filter((s) => !isClosedShowing(s));
  const nextShowing = activeShowings
    .filter((s) => s.scheduled_at && new Date(s.scheduled_at).getTime() >= Date.now())
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))[0] || null;
  const criteriaLine = formatBuyerCriteriaLine(client.buyer_profile);

  return (
    <>
      {nextShowing && (
        <div className="client-portal__upcoming" role="status">
          <Calendar size={16} />
          <span>
            <strong>Upcoming:</strong>{' '}
            {new Date(nextShowing.scheduled_at).toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}
            {' at '}
            {new Date(nextShowing.scheduled_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            {' — '}
            {nextShowing.property?.address || 'showing'}
          </span>
        </div>
      )}

      <section className="client-journey" aria-label="Your home journey">
        <div className="client-journey__chip">
          <span className="client-journey__num">{savedError ? '—' : savedProperties.length}</span>
          <span className="client-journey__label">
            Saved {savedProperties.length === 1 ? 'home' : 'homes'}
          </span>
        </div>
        <div className="client-journey__chip">
          <span className="client-journey__num">{showingsError ? '—' : activeShowings.length}</span>
          <span className="client-journey__label">
            Showing{showings.length === 1 ? '' : 's'} booked
          </span>
        </div>
        <div className="client-journey__chip">
          <span className="client-journey__num">{matchesError ? '—' : unseenCount}</span>
          <span className="client-journey__label">
            New match{unseenCount === 1 ? '' : 'es'}
          </span>
        </div>
        <div className="client-journey__chip client-journey__chip--search">
          <span className="client-journey__label">Your search</span>
          <span className="client-journey__num client-journey__num--sm">
            {criteriaLine || `Chat with ${aiName} to set your budget & areas`}
          </span>
        </div>
      </section>
    </>
  );
}

export function BuyerPortalPanels({
  client,
  slug,
  buyerData,
}) {
  const {
    saving,
    syncing,
    matching,
    saveMsg,
    matches,
    unseenCount,
    matchMsg,
    showings,
    profile,
    handleChange,
    handleSyncFromChat,
    handleSave,
    markSeen,
    dismissMatch,
    markAllSeen,
    referralSent,
    sendReferralRequest,
    runMatching,
    savedProperties,
    clearPropertyState,
    showingsError,
    matchesError,
    savedError,
  } = buyerData;

  const agentName = `${client.agent.first_name} ${client.agent.last_name}`;
  const aiName = client.agent.assistant_name || 'Ava';
  const activeShowings = showings.filter((s) => !isClosedShowing(s));
  const pastShowings = showings.filter((s) => isClosedShowing(s));
  const staleMatchCutoff = Date.now() - MATCH_STALE_DAYS * 24 * 60 * 60 * 1000;
  const freshMatches = matches.filter((m) => new Date(m.created_at).getTime() >= staleMatchCutoff);
  const staleMatches = matches.filter((m) => new Date(m.created_at).getTime() < staleMatchCutoff);
  const criteriaLine = formatBuyerCriteriaLine(client.buyer_profile);
  const narrativeSummary = getBuyerNarrativeSummary(client.buyer_profile);
  const showNarrative =
    narrativeSummary
    && (!criteriaLine || narrativeSummary.toLowerCase() !== criteriaLine.toLowerCase());
  const [criteriaExpanded, setCriteriaExpanded] = useState(() => !criteriaLine);

  return (
    <>
      <section id="showings" className="client-portal__panel client-portal__panel--showings">
        <h2>
          <Calendar size={18} /> Your showing requests
        </h2>
        <p className="client-portal__panel-desc">
          Requests you made in chat with {aiName}. {client.agent.first_name} will confirm times with you.
        </p>
        {showingsError ? (
          <p className="client-form__error">Couldn&apos;t load your showing requests. Refresh the page to try again.</p>
        ) : showings.length === 0 ? (
          <p className="client-portal__panel-desc">
            No showing requests on file yet. Ask {aiName} to book a viewing in chat, then refresh from chat below.
          </p>
        ) : (
          <>
            {activeShowings.length === 0 ? (
              <p className="client-portal__panel-desc">
                No active showing requests right now.
              </p>
            ) : (
              <ul className="client-showing-list">
                {activeShowings.map((s) => <ShowingCard key={s.id} showing={s} />)}
              </ul>
            )}
            {pastShowings.length > 0 && (
              <details className="client-showing-past">
                <summary>Past requests ({pastShowings.length})</summary>
                <ul className="client-showing-list client-showing-list--past">
                  {pastShowings.map((s) => <ShowingCard key={s.id} showing={s} />)}
                </ul>
              </details>
            )}
          </>
        )}
      </section>

      <section className="client-portal__panel client-portal__panel--matches">
        <div className="client-portal__panel-head">
          <h2>
            <Bell size={18} /> New for you
          </h2>
          <div className="client-portal__panel-actions">
            {unseenCount > 0 && (
              <button type="button" className="client-form__secondary" onClick={markAllSeen}>
                Mark all read
              </button>
            )}
            <button type="button" className="client-form__secondary" onClick={runMatching} disabled={matching}>
              <RefreshCw size={14} className={matching ? 'client-spin' : ''} /> {matching ? 'Searching...' : 'Refresh'}
            </button>
          </div>
        </div>
        {unseenCount > 0 && <p className="client-portal__badge">{unseenCount} new</p>}
        {matchMsg && (
          <p className={`client-portal__match-msg client-portal__match-msg--${matchMsg.variant || 'info'}`}>
            {matchMsg.text}
          </p>
        )}
        {matchesError ? (
          <p className="client-form__error">Couldn&apos;t load matches. Tap Refresh to try again.</p>
        ) : matches.length === 0 ? (
          <p className="client-portal__panel-desc">
            {profile.matching_enabled
              ? `No matches yet. Set your budget and areas, then tap Refresh — or chat with ${aiName} to refine your search.`
              : 'Turn on “Keep searching for new listings” below to see matches here.'}
          </p>
        ) : (
          <>
            {freshMatches.length === 0 ? (
              <p className="client-portal__panel-desc">
                Nothing new right now — see older matches below.
              </p>
            ) : (
              <ul className="client-match-list">
                {freshMatches.map((m) => (
                  <MatchCard key={m.id} match={m} slug={slug} aiName={aiName} agentName={agentName} markSeen={markSeen} dismissMatch={dismissMatch} />
                ))}
              </ul>
            )}
            {staleMatches.length > 0 && (
              <details className="client-showing-past">
                <summary>Older matches ({staleMatches.length})</summary>
                <ul className="client-match-list client-match-list--past">
                  {staleMatches.map((m) => (
                    <MatchCard key={m.id} match={m} slug={slug} aiName={aiName} agentName={agentName} markSeen={markSeen} dismissMatch={dismissMatch} />
                  ))}
                </ul>
              </details>
            )}
          </>
        )}
      </section>

      <section className="client-portal__panel client-portal__panel--saved">
        <h2>
          <Home size={18} /> Saved listings
        </h2>
        <p className="client-portal__panel-desc">
          Listings you saved while chatting with {aiName}. Passed listings are hidden from your matches.
        </p>
        {savedError ? (
          <p className="client-form__error">Couldn&apos;t load your saved homes. Refresh the page to try again.</p>
        ) : savedProperties.length === 0 ? (
          <p className="client-portal__panel-desc">
            Nothing saved yet. Tap “Save” on a listing in chat to keep it here.
          </p>
        ) : (
          <ul className="client-showing-list">
            {savedProperties.map((s) => (
              <li key={s.property_id} className="client-showing-card">
                <div className="client-showing-card__address">
                  {s.address_line1 || 'Property'}
                  {s.city ? `, ${s.city}` : ''}
                </div>
                <div className="client-showing-card__meta">
                  {s.list_price != null && (
                    <>${Number(s.list_price).toLocaleString()}{' · '}</>
                  )}
                  {s.bedrooms != null && <>{s.bedrooms} bed · </>}
                  {s.bathrooms != null && <>{s.bathrooms} bath</>}
                  {s.mls_number ? <> · MLS® {s.mls_number}</> : null}
                </div>
                <div className="client-match-card__actions">
                  <Link
                    to={buildAskPropertyChatPath(slug, {
                      propertyId: s.property_id,
                      mlsNumber: s.mls_number,
                    })}
                    className="client-match-card__cta"
                  >
                    Ask {aiName}
                  </Link>
                  <button
                    type="button"
                    className="client-match-card__dismiss"
                    onClick={() => clearPropertyState(s.property_id)}
                    aria-label="Remove from saved"
                  >
                    <X size={16} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="client-portal__panel">
        <div className="client-portal__panel-head">
          <h2>
            <Home size={18} /> Search criteria
          </h2>
            {criteriaLine && (
              <button type="button" className="client-form__secondary" onClick={() => setCriteriaExpanded((v) => !v)}>
                {criteriaExpanded ? 'Hide' : 'Edit'}
              </button>
            )}
          </div>
          <p className="client-portal__panel-desc">
            Budget, areas, and bedrooms, plus what the home must have, what to avoid, and what would be nice.
          </p>
          {criteriaLine && (
            <p className="client-portal__summary client-portal__summary--criteria">
              <strong>Your search:</strong> {criteriaLine}
            </p>
          )}
          <p className="client-portal__detail"><strong>Must-haves:</strong> {wishText(client.buyer_profile?.must_haves)}</p>
          <p className="client-portal__detail"><strong>Things to avoid:</strong> {wishText(client.buyer_profile?.deal_breakers)}</p>
          <p className="client-portal__detail"><strong>Nice to have, but not a must:</strong> {wishText(client.buyer_profile?.nice_to_haves)}</p>
          {showNarrative && <p className="client-portal__summary">{narrativeSummary}</p>}
          {!client.buyer_profile?.price_max && !client.buyer_profile?.areas?.length && (
            <p className="client-portal__panel-desc" style={{ color: '#8a6a30' }}>
              As you chat with {aiName} about what you&apos;re looking for, your budget, preferred
              areas, must-haves, things to avoid, and nice-to-haves will appear here — and you can edit them anytime.
            </p>
          )}
          {criteriaExpanded && (
          <form className="client-form" onSubmit={handleSave}>
            <div className="client-form__row">
              <label>
                Min price ($)
                <input name="price_min" type="number" value={profile.price_min} onChange={handleChange} />
              </label>
              <label>
                Max price ($)
                <input name="price_max" type="number" value={profile.price_max} onChange={handleChange} />
              </label>
            </div>
            <div className="client-form__row">
              <label>
                Min bedrooms
                <input name="bedrooms_min" type="number" min="0" value={profile.bedrooms_min} onChange={handleChange} />
              </label>
              <label>
                Min bathrooms
                <input name="bathrooms_min" type="number" min="0" step="0.5" value={profile.bathrooms_min} onChange={handleChange} />
              </label>
            </div>
            <label>
              Property type
              <select name="property_type" value={profile.property_type} onChange={handleChange}>
                <option value="">No preference</option>
                <option value="single_family">Detached / single-family</option>
                <option value="townhouse">Townhouse</option>
                <option value="condo">Condo / apartment</option>
                <option value="semi_detached">Semi-detached</option>
              </select>
            </label>
            <label>
              Pre-approved for a mortgage?
              <select name="pre_approved" value={profile.pre_approved} onChange={handleChange}>
                <option value="">Prefer not to say</option>
                <option value="yes">Yes, pre-approved</option>
                <option value="no">Not yet</option>
                <option value="not_yet">Working on it</option>
              </select>
            </label>
            <label>
              Areas (comma-separated)
              <input name="areas" type="text" value={profile.areas} onChange={handleChange} placeholder="St. John's, Mount Pearl" />
            </label>
            <label>
              Must-haves (comma-separated)
              <input name="must_haves" type="text" value={profile.must_haves} onChange={handleChange} placeholder="parking, fenced yard" />
            </label>
            <label>
              Things to avoid (comma-separated)
              <input name="deal_breakers" type="text" value={profile.deal_breakers} onChange={handleChange} placeholder="oil heat, a busy road" />
            </label>
            <label>
              Nice to have, but not a must (comma-separated)
              <input name="nice_to_haves" type="text" value={profile.nice_to_haves} onChange={handleChange} placeholder="a garage, a fireplace" />
            </label>
            <label>
              Timeline
              <input name="timeline" type="text" value={profile.timeline} onChange={handleChange} placeholder="e.g. 3–6 months" />
            </label>
            <div className="client-form__checks">
              <label className="client-form__check">
                <input type="checkbox" name="matching_enabled" checked={profile.matching_enabled} onChange={handleChange} />
                Keep searching for new listings
              </label>
              <label className="client-form__check">
                <input type="checkbox" name="alert_email" checked={profile.alert_email} onChange={handleChange} />
                Email me when new properties match my criteria
              </label>
            </div>
            {saveMsg && (
              <p className={saveMsg.startsWith('Saved') || saveMsg.startsWith('Updated') ? 'client-form__success' : 'client-form__error'}>
                {saveMsg}
              </p>
            )}
            <div className="client-portal__actions">
              <button type="submit" className="client-form__submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save criteria'}
              </button>
            </div>
          </form>
          )}
          <p className="client-portal__sync-hint">
            Pulls search criteria and showing requests from your chat with {aiName}.{' '}
            <button
              type="button"
              className="client-portal__sync-link"
              onClick={() => handleSyncFromChat(aiName)}
              disabled={syncing}
            >
              {syncing ? 'refreshing...' : 'Refresh buying details from chat'}
            </button>
          </p>
      </section>

      <section className="client-portal__panel">
        <h2>
          <Home size={18} /> Your closing team
        </h2>
        <p className="client-portal__panel-desc">
          Two people you'll want lined up before an offer comes together.
        </p>
        <div className="client-form client-form__row">
          <label>
            Do you have a real estate lawyer?
            <select name="has_lawyer" value={profile.has_lawyer} onChange={handleChange}>
              <option value="">Prefer not to say</option>
              <option value="yes">Yes, already have one</option>
              <option value="not_yet">Working on it</option>
              <option value="no">Not yet</option>
            </select>
            {profile.has_lawyer === 'no' && (
              referralSent.lawyer ? (
                <span className="client-portal__referral-sent">Asked {agentName.split(' ')[0]} — check Messages above.</span>
              ) : (
                <button type="button" className="client-form__secondary client-portal__referral-btn" onClick={() => sendReferralRequest('lawyer')}>
                  Ask {agentName.split(' ')[0]} for a referral
                </button>
              )
            )}
          </label>
          <label>
            Do you have a mortgage broker?
            <select name="has_mortgage_broker" value={profile.has_mortgage_broker} onChange={handleChange}>
              <option value="">Prefer not to say</option>
              <option value="yes">Yes, already have one</option>
              <option value="not_yet">Working on it</option>
              <option value="no">Not yet</option>
            </select>
            {profile.has_mortgage_broker === 'no' && (
              referralSent.mortgage_broker ? (
                <span className="client-portal__referral-sent">Asked {agentName.split(' ')[0]} — check Messages above.</span>
              ) : (
                <button type="button" className="client-form__secondary client-portal__referral-btn" onClick={() => sendReferralRequest('mortgage_broker')}>
                  Ask {agentName.split(' ')[0]} for a referral
                </button>
              )
            )}
          </label>
        </div>

        <details className="client-showing-past">
          <summary>What to expect when buying in Newfoundland &amp; Labrador</summary>
          <ul className="client-portal__nl-facts">
            <li>A lawyer isn't legally required to buy in NL (unlike Ontario or Alberta, where it is), but nearly everyone uses one — they handle the title search, the deed transfer, and closing funds. Typical fee: roughly $1,100–$1,800.</li>
            <li>NL doesn't charge a land transfer tax. Instead there's a Registry of Deeds registration fee, usually in the $400–$600 range for a typical home — no first-time-buyer rebate, but it's one of the lowest closing-cost provinces in Canada.</li>
            <li>Budget roughly 3–4% of the purchase price for total closing costs: legal fees, the registration fee, a home inspection, and your first year of home insurance (required by your lender before closing).</li>
            <li>Do a final walkthrough 24–48 hours before closing to confirm the home's condition matches what you agreed to.</li>
          </ul>
          <p className="client-portal__nl-facts-disclaimer">
            General information, not legal or financial advice — your lawyer and mortgage broker will confirm the specifics for your purchase.
          </p>
        </details>
      </section>
    </>
  );
}
